import {Directive, OnDestroy, inject} from '@angular/core';
import {Subscription, finalize} from 'rxjs';
import {INode, NgeExplorerConfig} from '../../shared/types';
import {CONFIG} from '../../shared/providers';
import {ExplorerService} from '../../services/explorer.service';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { TranslateService } from '@ngx-translate/core';

/** Stati di analisi del documento lato RAG (allineati al backend). */
export type RagStatusValue = 'not_analyzed' | 'pending' | 'analyzed' | 'error';

/** content-type dei documenti che possono essere inviati in analisi al RAG. */
const RAG_SUPPORTED_CONTENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv',
  'application/csv',
  'application/vnd.ms-excel',
];

/** estensioni accettate come fallback quando il content-type non è valorizzato. */
const RAG_SUPPORTED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.csv'];

@Directive()
export class BaseView implements OnDestroy {
  protected selection = new Set<number>();
  protected selectedNodes: INode[] = [];
  protected items: INode[] = [];
  protected parentItem!: INode;
  protected parentIsHome = false;
  protected parentObj!: INode;

  draggingelement:boolean = false

  /** id dei nodi con una operazione RAG (invio/rimozione) in corso lato client. */
  protected ragProcessing = new Set<number>();

  parentdIds: string[] = [];
  connectedIds:string[] = [];
  protected files: INode[] = [];
  protected dirs: INode[] = [];
  protected dragging = false;
  protected subs = new Subscription();
  protected shiftSelectionStartId: number | undefined;

  protected explorerService: ExplorerService = inject(ExplorerService);
  protected config: NgeExplorerConfig = inject(CONFIG);
  protected translate: TranslateService = inject(TranslateService);

  constructor() {
    this.subs.add(
      this.explorerService.openedDir$.subscribe((nodes) => {
        this.items = nodes ? nodes.children : [];
        this.parentItem = nodes ? nodes : { id: 0, name: this.config.homeNodeName, parentId: 0, data: { name: this.config.homeNodeName }, isLeaf: false, expanded: false, children: [] };
        this.files = this.items.filter((i) => i.isLeaf);
        this.dirs = this.items.filter((i) => !i.isLeaf);
        this.parentdIds =  this.connectedIds = this.connectedIds.concat(this.parentItem.id.toString());
        // this.parentIsHome = this.parentItem.name === this.config.homeNodeName;
        this.parentIsHome = !this.parentItem.parentId
        this.parentObj = this.explorerService.getNode(this.parentItem.parentId)
        if(this.parentObj){
          this.dirs.push(this.parentObj)
        }
        this.connectedIds = this.dirs.map((i) => i.id.toString());


      })
    );

    this.subs.add(
      this.explorerService.selection$.subscribe((nodes) => {
        this.selection.clear();
        if (nodes) {
          this.selection = new Set(nodes.map((n) => n.id));
          this.selectedNodes = nodes;
        }
      })
    );
  }

  draggingState(event:any, state:boolean) {
    console.log(event)
    this.draggingelement = state
  }

  onDragStart(event:any,item:INode) {
     console.log(event, item)
     let eventMouse = event.event as MouseEvent
     this.select(eventMouse, item, true);
    // if (this.selection.size === 0) {
    //     this.select(event,item)
    //   }
    }

  select(event: MouseEvent, item: INode, isDragging?: boolean) {
    const shiftKeyPressed = event.shiftKey;
    const metaKeyPressed = event.metaKey || event.ctrlKey;

    if (this.config.multipleSelection && shiftKeyPressed) {
      if (this.selection.size === 0) {
        this.selection.add(item.id);
        this.shiftSelectionStartId = item.id;
      } else {
        this.selection.clear();
        const headIndex = this.items.findIndex((i) => i.id === this.shiftSelectionStartId);
        const currentIndex = this.items.findIndex((i) => i.id === item.id);

        const start = Math.min(headIndex, currentIndex);
        const end = Math.max(headIndex, currentIndex);

        for (let i = start; i <= end; i++) {
          this.selection.add(this.items[i].id);
        }
      }
    } else {
      if (this.config.multipleSelection && metaKeyPressed) {

        if (this.selection.has(item.id) ) {
          if(!isDragging){
            this.selection.delete(item.id);
          }
        } else {
          this.selection.add(item.id);
        }
      } else {
        this.selection.clear();
        this.shiftSelectionStartId = item.id;
        this.selection.add(item.id);
      }
    }

    const nodes = this.items.filter((i) => this.selection.has(i.id));
    this.selectedNodes = nodes;
    this.explorerService.select(nodes);
  }

  open(event: MouseEvent, item: INode) {
    const openFile = this.config?.openFileOnDblClick || false;
    if (openFile) {
      this._openFileOrNode(event, item)
    } else {
      this._openViewFile(event, item)
    }
  }

  /**
   * Opens a node or file for view if it is a leaf
   * or opens a node for browse if it is not a leaf
   * @param event Mouse event
   * @param item Node to open
   */
  _openViewFile(event: MouseEvent, item: INode) {
    if (item.isLeaf) {
      this.explorerService.viewFile();
    } else {
      const metaKeyPressed = event.metaKey || event.ctrlKey || event.shiftKey;
      if (!metaKeyPressed) {
        this.explorerService.openNode(item.id);
      }
    }
  }

  /**
     * Open node on double click or single click without modifier keys (shift, control, meta)
     * @param event Mouse event
     * @param item Node to open
     */
  _openFileOrNode(event: MouseEvent, item: INode) {
    const metaKeyPressed = event.metaKey || event.ctrlKey || event.shiftKey;
    if (!metaKeyPressed) {
        if(item.isLeaf) {
            this.explorerService.openFile(item);
        } else {
            this.explorerService.openNode(item.id);
        }
    }
}

  isSelected(item: INode) {
    return this.selection.has(item.id);
  }

  emptySpaceClick(): void {
    this.explorerService.select([]);
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }


  public onDragDrop(event: CdkDragDrop<INode,any, any>, dropAreaItem:INode) {
    console.log(event.item, event.container, dropAreaItem, event, this.selectedNodes);
    if (!event.item.data.length) {
      return
    }
    // TODO: gestire singola chiamata per fare la move di tutti i files
    event.item.data.forEach((node:INode) => {
      if(node.parentId === dropAreaItem.id) {
        return
      }
      if(node.data['id'] === dropAreaItem.data['id']) {
        return
      }
      this.explorerService.moveItemToFolder(node.data, dropAreaItem.data['id'])
      this.items = this.items.filter(i => i.id !== node.id)
    })
  }

  public onDragDropParent(event: CdkDragDrop<INode,any, any>, dropAreaItem:INode) {
    console.log(event.item, event.container, dropAreaItem, event, this.selectedNodes);
    if(!this.parentObj || !event.item.data.length) {
      return
    }
    let realParent = this.parentObj

    // TODO: gestire singola chiamata per fare la move di tutti i files
    event.item.data.forEach((node:INode) => {
      if(node.parentId === realParent.id) {
        return
      }
      if(node.data['id'] === dropAreaItem.data['id']) {
        return
      }
      let parentId=  realParent.data ? realParent.data['id'] : 0
      this.explorerService.moveItemToFolder(node.data, parentId);
      this.items = this.items.filter(i => i.id !== node.id)
    })
  }

  connectedButMe(item:INode) {
    return this.connectedIds.filter((id) => id !== item.id.toString());
  }


  moveNodeToFolder(node: INode, targetFolder: INode) {
    console.log(`Sposto ${node.name} nella cartella ${targetFolder.name}`, node, targetFolder);

    // aggiorna proprietà localmente
    node.parentId = targetFolder.id;

    // eventualmente aggiorna anche la lista dei figli della cartella
    if (!targetFolder.children) {
      targetFolder.children = [];
    }
    targetFolder.children.push(node);
  }

  onDrop(event: CdkDragDrop<INode[]>) {
    const draggedNodes: INode[] = event.item.data;

    // Supponiamo che siamo nella root (senza cartella attiva)
    const targetFolderId = null;

    for (const node of draggedNodes) {
      this.moveNodeToFolder(node, node);
    }

    this.selectedNodes = [];
  }

  // ---------------------------------------------------------------------------
  // RAG: analisi documenti
  // ---------------------------------------------------------------------------

  /** Stato RAG corrente del nodo (default: mai inviato). */
  ragStatus(item: INode): RagStatusValue {
    return (item?.data?.['rag_status'] as RagStatusValue) || 'not_analyzed';
  }

  /** Vero se il documento è di un tipo inviabile in analisi (pdf, word, csv). */
  isRagSupported(item: INode): boolean {
    if (!item?.isLeaf) {
      return false;
    }
    const contentType = (item.data?.['content_type'] || '').toString().toLowerCase();
    if (contentType && RAG_SUPPORTED_CONTENT_TYPES.includes(contentType)) {
      return true;
    }
    const name = (item.data?.['name'] || '').toString().toLowerCase();
    return RAG_SUPPORTED_EXTENSIONS.some((ext) => name.endsWith(ext));
  }

  /** Vero se è in corso una operazione RAG (client) o l'analisi è pending (server). */
  isRagProcessing(item: INode): boolean {
    return this.ragProcessing.has(item.id) || this.ragStatus(item) === 'pending';
  }

  /** Il documento può essere inviato/reinviato in analisi. */
  canSendToRag(item: INode): boolean {
    if (!this.isRagSupported(item) || this.isRagProcessing(item)) {
      return false;
    }
    const status = this.ragStatus(item);
    return status === 'not_analyzed' || status === 'error';
  }

  /** Il documento è nella conoscenza e può esserne rimosso. */
  canRemoveFromRag(item: INode): boolean {
    return this.isRagSupported(item) && !this.isRagProcessing(item) && this.ragStatus(item) === 'analyzed';
  }

  /** Etichetta leggibile dello stato di elaborazione AI. */
  ragStatusLabel(item: INode): string {
    if (this.ragProcessing.has(item.id)) {
      return this.translate.instant('filemanager.rag.processing');
    }
    switch (this.ragStatus(item)) {
      case 'analyzed':
        return this.translate.instant('filemanager.rag.indexed');
      case 'pending':
        return this.translate.instant('filemanager.rag.pending');
      case 'error':
        return this.translate.instant('filemanager.rag.error');
      default:
        return this.translate.instant('filemanager.rag.not_indexed');
    }
  }

  /** Etichetta del pulsante di invio (Elabora / Rielabora in caso di errore). */
  ragSendLabel(item: INode): string {
    return this.translate.instant(
      this.ragStatus(item) === 'error' ? 'filemanager.rag.resend' : 'filemanager.rag.send'
    );
  }

  /** Titolo/tooltip per il badge di stato (es. "Stato AI: Indicizzato"). */
  ragStatusTitle(item: INode): string {
    return `${this.translate.instant('filemanager.rag.status_column')}: ${this.ragStatusLabel(item)}`;
  }

  /** Titolo del pulsante di rimozione dall'indice AI. */
  ragRemoveLabel(): string {
    return this.translate.instant('filemanager.rag.remove_title');
  }

  /** Suffisso classe CSS per la colorazione del badge di stato. */
  ragStatusClass(item: INode): string {
    return this.ragProcessing.has(item.id) ? 'pending' : this.ragStatus(item);
  }

  /** Invia (o reinvia) il documento in analisi. */
  sendToRag(event: Event, item: INode): void {
    event.stopPropagation();
    if (!this.canSendToRag(item)) {
      return;
    }
    if (!confirm(this.translate.instant('filemanager.rag.confirm_send', {name: item.name}))) {
      return;
    }
    this.ragProcessing.add(item.id);
    this.explorerService
      .sendToRag(item.data)
      .pipe(finalize(() => this.ragProcessing.delete(item.id)))
      .subscribe({error: () => {}});
  }

  /** Rimuove il documento dalla conoscenza del RAG. */
  removeFromRag(event: Event, item: INode): void {
    event.stopPropagation();
    if (!this.canRemoveFromRag(item)) {
      return;
    }
    if (!confirm(this.translate.instant('filemanager.rag.confirm_remove', {name: item.name}))) {
      return;
    }
    this.ragProcessing.add(item.id);
    this.explorerService
      .removeFromRag(item.data)
      .pipe(finalize(() => this.ragProcessing.delete(item.id)))
      .subscribe({error: () => {}});
  }
}
